import { v4 as uuidv4 } from 'uuid'
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import LineString from 'ol/geom/LineString'
import { latLngProjection, Renderer } from '@/Renderer'
import Point from 'ol/geom/Point'
import LineFeatureSource from '@/LineFeatureSource'
import TrackLineFeature from '@/TrackLineFeature'
import AnchorPointFeature from '@/AnchorPointFeature'
import VectorLayer from 'ol/layer/Vector'
import { getLength } from 'ol/sphere'
import RestoreTrackData from './RestoreTrackData'
import * as _ from 'lodash' 
import { AnchorPointFeatureSource } from './AnchorPointFeatureSource'
import { DefaultColour } from './Colours'
import { Stroke, Style } from 'ol/style'
import ReinstateTrackData from './ReinstateTrackData'
import TrackVectorLayer from './TrackVectorLayer'
import { TrackVectorLayerType } from './TrackVectorLayerType'
import { Styles } from './Styles'
import OrderHelper from './OrderHelper'
import { DistanceUnit } from './DistanceUnits'
import Feature from 'ol/Feature'
import { Overlay } from 'ol'
import { Coordinate, equals } from 'ol/coordinate'
import SplitHelper from './SplitHelper'
import PreUndoEvent from './PreUndoEvent'
import CryptoJS from 'crypto-js'
import PointFeatureSource from './PointFeatureSource';
import TrackPointFeature from './TrackPointFeature';
import { containsCoordinate, Extent } from 'ol/extent';
import VectorSource from 'ol/source/Vector';
import LineStringFeature from './LineStringFeature';

interface HistoryStackItem {
    trackLngLats: LineString[];
    dotLngLats: Point[];
}

interface DistanceMarkerInfo {
    distance: number;
    position: Coordinate;
}

const trackHashBlank = '<blank>'

export class TrackEntity {
    static nextTrackId = 1

    name = 'Untitled' + (TrackEntity.nextTrackId++).toString()
    value = uuidv4()
    totalDistance = 0
    zIndex = 0 // gets set when currentTrack or tracks collection is modified
    trackLngLatFeatures: LineStringFeature[] = []
    get trackLngLats(): LineString[] {
        return this.trackLngLatFeatures.map(g => g.getGeometry())
    }
    dotLngLats: Point[] = []
    layers: VectorLayer<VectorSource<any>>[] = []
    history: HistoryStackItem[] = []
    overallLineString: LineString|null = null
    trackHash = trackHashBlank
    private isShowingTrackpoints = false
    
    private trackFeatureSource = new LineFeatureSource(this, { features: [] })
    
    private trackPointsFeatureSource = new PointFeatureSource()
    private trackPointsFeatureSourceDirty = false
    private dotsFeatureSource = new AnchorPointFeatureSource({ features: [] })
    private distanceMarkerOverlays: Overlay[] = []

    private individualStraightLinesFeatureSource = new VectorSource<LineString>()

    renderer: Renderer<any>

    constructor(renderer: Renderer<any>) {
        this.renderer = renderer
        this.trackFeatureSource.on('change', _.debounce(e => {
            this.recalculateDistance()
            if(this.isShowingTrackpoints) {
                this.updateTrackpointsFeatureSource()
            } else {
                this.trackPointsFeatureSourceDirty = true // update it if/when we turn it on
            }
        }))
    }

    get hasMoreThan10000Points() {
        let points = 0;
        this.trackFeatureSource.forEachFeature(f => {
            const g = f.getGeometry();
            if(g) {
                points += g.getCoordinates().length;
                return points > 10000; //stop iterating if already gone over
            }
        })
        return points > 10000;
    }

    flatten() {
        // should call saveFeaturesToLngLats prior to calling this
        const coords = this.trackLngLatFeatures.flatMap(t => t.getGeometry().getCoordinates());
        const singleLngLatFeature = new LineStringFeature(new LineString(coords));
        this.dotLngLats.splice(1, this.dotLngLats.length - 2);
        this.trackLngLatFeatures = [singleLngLatFeature]
    }

    get showingTrackpoints() {
        return this.isShowingTrackpoints
    }
    set showingTrackpoints(val: boolean) {
        this.isShowingTrackpoints = val
        if(val && this.trackPointsFeatureSourceDirty) {
            this.updateTrackpointsFeatureSource()
        }
    }

    setAnchorCoord(index: number, coord: Coordinate) {
        this.dotsFeatureSource.getFeatures()[index]?.getGeometry().setCoordinates(coord)
    }

    private updateTrackpointsFeatureSource() {
        this.trackPointsFeatureSource.clear()
        if(this.overallLineString !== null) {
            let overallIndex = 0
            const trackFeatures = this.trackFeatureSource.getFeatures()
            for(let trackFeatureIndex = 0; trackFeatureIndex < trackFeatures.length; trackFeatureIndex++) {
                const trackLineFeature = trackFeatures[trackFeatureIndex]
                const coords = trackLineFeature.getGeometry().getCoordinates()
                for(let indexWithinFeature = 0; indexWithinFeature < coords.length; indexWithinFeature++) {
                    let anchorIndex: number|null
                    if(indexWithinFeature === 0) {
                        anchorIndex = trackFeatureIndex // first point in a segment
                    } else if (indexWithinFeature === coords.length - 1) {
                        anchorIndex = trackFeatureIndex + 1 // last point in a segment
                    } else anchorIndex = null
                    this.trackPointsFeatureSource.addFeature(new TrackPointFeature(new Point(coords[indexWithinFeature]), this, overallIndex++, trackLineFeature, indexWithinFeature, anchorIndex))
                }
            }
        }
        this.trackPointsFeatureSourceDirty = false
    }


    
    getOverallIndex(index: number, inFeature: TrackLineFeature) {
        const allFeatures = this.trackFeatureSource.getFeatures()
        const featureIndex = allFeatures.indexOf(inFeature)
        if(featureIndex === -1) {
            throw new Error("The supplied feature was not foudn in trackFeatureSource")
        }
        return _.sumBy(allFeatures.slice(0, featureIndex), f => f.getGeometry().getCoordinates().length) + index
    }

    getCoordAtProportion(proportion: number): Coordinate|null {
        if(!this.overallLineString) {
            return null
        }
        return this.overallLineString.getCoordinateAt(proportion)
    }

    type = 'track'
    colourInternal = DefaultColour

    asLatLongGeoJsonFeatureCollection(): GeoJSONFeatureCollection {
        const geoJson = new GeoJSON()
        return geoJson.writeFeaturesObject(this.trackFeatureSource.getFeatures(), {
            dataProjection: latLngProjection,
            featureProjection: this.renderer.projection,
            decimals: 5
        })
    }

    get hasSegments() {
        return this.overallLineString && this.overallLineString.getCoordinates().length > 0
    }

    addDot(dotGeometry: Point, atIndex?: number) {
        const order = OrderHelper.getOrderOfNewItem(this.getDots(), atIndex)
        const dot = new AnchorPointFeature(dotGeometry, this, order)
        dot.track = this

        if(atIndex !== undefined) {
            const features = this.dotsFeatureSource.getFeatures()
            features.splice(atIndex, 0, dot) // this *doesn't* add it to the features collection, just enables us to the order correctly (as getFeatures returns the existing ones in the correct order)
            features.forEach((d,i) => d.order = i)
        } else {
            dot.order = this.dotsFeatureSource.maxOrder + 1
        }

        this.dotsFeatureSource.addFeature(dot)

        // sanity check:
        const newFeatures = this.dotsFeatureSource.getFeatures()
        if(atIndex !== undefined) {
            if(newFeatures[atIndex] !== dot) {
                console.error('didn\'t insert dot at correct index!')
            }
        }
    }

    getCoordinate(trackLineFeatureIndex: number, coordinateIndex: number): Coordinate|null {
        const feature = _.nth(this.trackFeatureSource.getFeatures(), trackLineFeatureIndex)  // if negative, count from the end
        if(!feature) return null
        const coords = feature.getGeometry().getCoordinates()
        const coord = _.nth(coords, coordinateIndex) // if negative, count from the end
        return coord ?? null
    }

    setCoordinate(trackLineFeatureIndex: number, coordinateIndex: number, coord: Coordinate) {
        this.trackFeatureSource.getFeatures()[trackLineFeatureIndex]?.setCoordinate(coordinateIndex, coord)
    }

    addSegment(segmentGeometry: LineString, atIndex?: number){
        const order = OrderHelper.getOrderOfNewItem(this.trackFeatureSource.getFeatures(), atIndex)
        const segment = new TrackLineFeature(segmentGeometry, this, order)
        segment.track = this
        
        if(atIndex !== undefined) {
            const features = this.trackFeatureSource.getFeatures()
            features.splice(atIndex, 0, segment) // this *doesn't* add it to the features collection, just enables us to the order correctly (as getFeatures returns the existing ones in the correct order)
            features.forEach((d,i) => d.order = i)
        } else {
            segment.order = this.trackFeatureSource.maxOrder + 1
        }
        this.trackFeatureSource.addFeature(segment)

        if(atIndex !== undefined) {
            if(this.trackFeatureSource.getFeatures()[atIndex] !== segment) {
                console.error('didn\'t insert segment at correct index!')
            }
        }
    }

    addDots(dots: AnchorPointFeature[]) {
        let nextOrder = this.dotsFeatureSource.maxOrder
        dots.forEach(d => {
            d.track = this
            d.order = ++nextOrder
        })
        this.dotsFeatureSource.addFeatures(dots)
    }

    addSegments(segments: TrackLineFeature[]) {
        let nextOrder = _.max(this.getSegments().map(t => t.order)) || 0
        segments.forEach(s => {
            s.track = this
            s.order = ++nextOrder
        })
        this.trackFeatureSource.addFeatures(segments)
    }

    getDots() {
        return this.dotsFeatureSource.getFeatures()
    }

    getSegments() {
        return this.trackFeatureSource.getFeatures()
    }

    removeDot(dot: AnchorPointFeature) {
        this.dotsFeatureSource.removeFeature(dot)
    }

    removeSegment(segment: TrackLineFeature) {
        this.trackFeatureSource.removeFeature(segment)
    }

    removeTrackpoint(trackpoint: TrackPointFeature, addPreUndoEvent: (e: PreUndoEvent) => void) {
        const geometry = trackpoint.trackLineFeature.getGeometry()
        const coords = geometry.getCoordinates()
        const index = coords.findIndex(c => equals(c, trackpoint.getGeometry().getCoordinates()))
        if(index > 0 && index < coords.length - 1) { // possibly could enable removal of the first/last but it'd be fiddly
            addPreUndoEvent(new PreUndoEvent([this.getRestoreData(this.renderer)], 'remove trackpoint'))
            coords.splice(index, 1)
            geometry.setCoordinates(coords)
        } else {
            console.warn('trackpoint was not found in trackLineSegment')
        }
    }

    clearDots() {
        this.dotsFeatureSource.clear()
    }

    zIndexForLayer(zIndexOfLayerWithinTrack: number) {
        return (TrackEntity.numberOfZIndexes * this.zIndex) + zIndexOfLayerWithinTrack
    }
    static numberOfZIndexes = 3
    createLayerForTrackpoints() {
        return new TrackVectorLayer(this, TrackVectorLayerType.Trackpoints, 1, {
            source: this.trackPointsFeatureSource,
            style: Styles.Trackpoint
        })
    }

    createLayerForDots() {
        return new TrackVectorLayer(this, TrackVectorLayerType.Dots, 2, {
            source: this.dotsFeatureSource,
            style: Styles.MidAnchorDot
        })
    }

    createLayerForSegments() {
        return new TrackVectorLayer(this, TrackVectorLayerType.Track, 0, {
            source: this.trackFeatureSource
        })
    }

    get colour() {
        return this.colourInternal
    }
    
    set colour(value: string) {
        this.colourInternal = value || DefaultColour
        this.trackFeatureSource.forEachFeature(f => f.setStyle(this.GetStyle()))
    }

    GetStyle(): Style[] {
        if(this.colour === DefaultColour) {
            return this.renderer.GetDefaultTrackStyle(this.type)
        } else {
            return this.renderer.GetTrackStyleForColour(this.colour)
        }
    }

    GetHighlightStyle() {
        const highlightLine = new Style({
            stroke: new Stroke({
                width: 20,
                color: 'white'
            })
        })
        return [highlightLine].concat(this.GetStyle())
    }

    GetExtent() {
        return this.trackFeatureSource.getExtent()
    }

    highlight() {
        this.trackFeatureSource.forEachFeature(t => t.setStyle(this.GetHighlightStyle()))
        setTimeout(() => {
            this.trackFeatureSource.forEachFeature(t => t.setStyle(this.GetStyle()))
        }, 2000)
    }

    removeDistanceMarkersFromMap() {
        this.distanceMarkerOverlays.forEach(o => this.renderer.map.removeOverlay(o))
    }

    getDistanceMarkerPositionsWithGranularity(lineString: LineString, extent: Extent, trackDistInUnits: number, granularity: number) {
        const markerPositions: DistanceMarkerInfo[] = []
        for(let dist = 0; dist <= trackDistInUnits; dist += granularity ) {
            const proportionOfTrack = dist / trackDistInUnits
            const pos = lineString.getCoordinateAt(proportionOfTrack) //verified in calling method
            if(containsCoordinate(extent, pos)) {
                markerPositions.push({
                    distance: dist,
                    position: pos
                })
            }
        }
        return markerPositions
    }

    getDistanceMarkerPositions(lineString: LineString, extent: Extent, trackDistInUnits: number) {
        const distances = [100, 50, 20, 10, 1, 0.5, 0.1, 0.05, 0.01]
        for(let i = 0; i < distances.length; i++) {
            const markerPositions = this.getDistanceMarkerPositionsWithGranularity(lineString, extent, trackDistInUnits, distances[i])
            if(markerPositions.length >= 6 || i == distances.length - 1) {
                return markerPositions
            }
        }
        throw new Error("Continuity error - should return in loop")
    }

    ensureDistanceMarkersSet(du: DistanceUnit) {
        try {
            this.removeDistanceMarkersFromMap()
            this.distanceMarkerOverlays = []
            if(this.overallLineString !== null) { // should always be set (is recreated on change event)
                const trackDistInUnits = this.totalDistance / du.metres
                const markerPositions = this.getDistanceMarkerPositions(this.overallLineString, this.renderer.GetExtent(), trackDistInUnits)
                markerPositions.forEach(markerPosition => {
                    const markerElement = document.createElement('div')
                    markerElement.className = 'distancemarker'
                    markerElement.innerHTML = markerPosition.distance.toLocaleString()
                    const overlay = new Overlay({
                        positioning: 'center-center',
                        element: markerElement,
                        position: markerPosition.position
                    })                    
                    this.distanceMarkerOverlays.push(overlay)
                })
            }
        } catch(error) {
            console.error(error)
        }
    }

    showDistanceMarkers(du: DistanceUnit) {
        this.removeDistanceMarkersFromMap()
        this.ensureDistanceMarkersSet(du)
        this.distanceMarkerOverlays.forEach(o => {
            this.renderer.map.addOverlay(o)
        })
    }

    getDistanceAlong(coord: Coordinate): number {
        if(this.overallLineString !== null && this.totalDistance > 0) {
            const indexOfPointPreceeding = SplitHelper.getIndexOfPointPreceeding(this.overallLineString, coord)
            if(indexOfPointPreceeding >= 0) {
                const coordsUpToPointPreceeding = _.take(this.overallLineString.getCoordinates(), indexOfPointPreceeding + 1)
                const upToPointPreceeding = new LineString(coordsUpToPointPreceeding, this.overallLineString.getLayout())
                const lengthUpToPointPreceeding = getLength(upToPointPreceeding)
                const fromLastPointToCoord = getLength(new LineString([upToPointPreceeding.getLastCoordinate(), coord]))
                return lengthUpToPointPreceeding + fromLastPointToCoord
            }
        }
        return -1
    }

    getDistancesAlong(coord: Coordinate): number[] {
        const retval: number[] = []
        if(this.overallLineString !== null && this.totalDistance > 0) {
            const pointsOnSegment = SplitHelper.getPointsOnSegmentNear(this.overallLineString, coord, this.renderer)
            return pointsOnSegment.map(p => p.dist)
        }
        return retval
    }

    recalculateDistance() {
        let newTotalDistance = 0
        let trackHash = trackHashBlank
        const features = this.trackFeatureSource.getFeatures()
        
        if(features.length > 0) {
            let overallCoords = features[0].getGeometry().getCoordinates()
            for(let i = 1; i < features.length; i++) {
                overallCoords = overallCoords.concat(features[i].getGeometry().getCoordinates().slice(1)) // first one should be the same as the last of current
            }
            this.overallLineString = new LineString(overallCoords)
            this.individualStraightLinesFeatureSource.clear()
            
            const geoJson = new GeoJSON()
            const writeOptions = {
                decimals: 5,
                featureProjection: this.renderer.projection,
                dataProjection: latLngProjection
            }
            const geoJsonLineString = geoJson.writeFeatureObject(new Feature<LineString>(this.overallLineString), writeOptions)
            trackHash = CryptoJS.MD5(JSON.stringify(geoJsonLineString)).toString(CryptoJS.enc.Base64)
            newTotalDistance = getLength(this.overallLineString, { projection: this.renderer.projection })
        }
        this.totalDistance = newTotalDistance
        this.trackHash = trackHash
    }

    saveFeaturesToLngLats(renderer: Renderer<any>) {
        this.trackLngLatFeatures = this.getSegments().map((feature) => {
            const f = new LineStringFeature(renderer.TransformLineStringToLatLng(feature.getGeometry()))
            TrackEntity.copyPropertiesExceptGeometry(feature, f)
            return f
        })
        this.dotLngLats = this.getDots().map((feature) => renderer.TransformPointToLatLng(feature.getGeometry()!))
    }

    reverse(renderer: Renderer<any>) {
        this.saveFeaturesToLngLats(renderer)
        const restoreData = new RestoreTrackData(this.value, this.dotLngLats, this.trackLngLatFeatures)
        this.trackLngLatFeatures.reverse()
        this.trackLngLatFeatures.forEach(t => {
            const trackGeom = t.getGeometry()
            if(trackGeom) {
                trackGeom.setCoordinates(trackGeom.getCoordinates().reverse())
            }
            
            const props = t.getProperties()
            for(const key in props) {
                let propVal = props[key]
                if(propVal && propVal instanceof Array) {
                    propVal = propVal.reverse()
                    t.set(key, propVal) //TODO: check this works with times
                }
            }
        })
        this.dotLngLats.reverse()
        this.loadFeaturesFromLngLats(renderer)
        return restoreData
    }

    getRestoreData(renderer: Renderer<any>) {
        this.saveFeaturesToLngLats(renderer)
        return new RestoreTrackData(this.value, this.dotLngLats, this.trackLngLatFeatures)
    }

    getReinstateData(renderer: Renderer<any>) {
        this.saveFeaturesToLngLats(renderer)
        return new ReinstateTrackData(this.value, this.dotLngLats, this.trackLngLatFeatures, this.name, this.colour, this.type, this.zIndex)
    }

    restore(renderer: Renderer<any>, preUndoData: RestoreTrackData) {
        this.trackLngLatFeatures = preUndoData.trackLngLatFeatures
        this.dotLngLats = preUndoData.dotLngLats
        this.loadFeaturesFromLngLats(renderer)
    }

    loadFeaturesFromLngLats(renderer: Renderer<any>) {
        this.renderer = renderer
        this.trackFeatureSource.clear()
        this.dotsFeatureSource.clear()
        this.trackFeatureSource.addFeatures(this.trackLngLatFeatures.map((lngLatLineStringFeature, index) =>  {
            const f = new TrackLineFeature(renderer.TransformLineStringFromLatLng(lngLatLineStringFeature.getGeometry()!), this, index)
            TrackEntity.copyPropertiesExceptGeometry(lngLatLineStringFeature, f)
            return f
        }))
        this.dotsFeatureSource.addFeatures(this.dotLngLats.map((point, index) => new AnchorPointFeature(renderer.TransformPointFromLatLng(point), this, index)))
    }

    static copyPropertiesExceptGeometry(source: Feature<LineString>, target: Feature<LineString>) {
        for(const prop in source.getProperties()) {
            if(prop !== 'geometry') {
                target.set(prop, source.get(prop))
            }
        }
    }

    isDegenerate() {
        return this.dotsFeatureSource.getFeatures().length === 0
    }


    splitAtCoordinate(latLngCoord: Coordinate, verifyTotalSanityCheck: number, addPreUndoEvent: (e: PreUndoEvent) => void) {
        if(this.overallLineString === null) {
            throw new Error("overallLineString not set")
        }

        if(verifyTotalSanityCheck !== _.sumBy(this.trackFeatureSource.getFeatures(), f => f.getGeometry().getCoordinates().length)) {
            throw new Error('verifyTotalSanityCheck not equal to total number of coordinates. Track may have changed since the information the coordinate is based on was created.')
        }

        

        const coord = this.renderer.TransformCoordFromLatLng(latLngCoord)
        if(coord === undefined) {
            throw new Error('coordinate is undefined')
        }
        const trackLineFeature = this.trackFeatureSource.getClosestFeatureToCoordinate(coord) as TrackLineFeature
        const split = SplitHelper.splitLineAt(trackLineFeature.getGeometry(), coord)

        if(split.secondTrackGeometry.getCoordinates().length <= 1) {
            console.warn('Cannot add an Anchor here as the nearest point is the end of the track.')
        }
        else if(split.firstTrackGeometry.getCoordinates().length <= 1) {
            console.warn('Cannot add an Anchor here as the nearest point is the start of the track. Try clicking nearer the middle of the track.')
        } else {
            addPreUndoEvent(new PreUndoEvent([this.getRestoreData(this.renderer)], 'add point'))
            trackLineFeature.setGeometry(split.firstTrackGeometry)
            const segmentIndex = this.getSegments().indexOf(trackLineFeature)
            this.addSegment(split.secondTrackGeometry, segmentIndex + 1)
            this.addDot(new Point(split.firstTrackGeometry.getLastCoordinate()), segmentIndex + 1)
        }
        return true
    }

    get text() { return this.name }
}
